import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import Chip from '@mui/material/Chip';

import { ReactComponent as PaperPlaneIcon } from '../assets/icons/message/paper_plane_icon.svg';
import { ReactComponent as YenIcon }        from '../assets/icons/message/yen_icon.svg';
import { ReactComponent as HandStarIcon }   from '../assets/icons/message/hand_star_icon.svg';

import { STAGE_GRADIENT } from '../constants/rankColors';
import { JobPredictionObject } from '../types';

interface EntrySentCelebrationProps {
  open: boolean;
  onClose: () => void;
  jobTitle?: string;
  prediction?: JobPredictionObject | null;
}

const EntrySentCelebration: React.FC<EntrySentCelebrationProps> = ({
  open,
  onClose,
  prediction,
}) => {
  if (!open) return null;
  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        // 付録C.5: 上側 青紫 → 下側 マゼンタ紫（左下→右上の対角）
        background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
        flexDirection: 'column',
        py: 3,
        px: 2,
      }}
    >
      {/* 装飾小円 + paper_plane_icon */}
      <Box
        sx={{
          width: 110,
          height: 110,
          borderRadius: '50%',
          border: '1.5px solid rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
          mt: 1,
        }}
      >
        <PaperPlaneIcon width={54} height={52} style={{ marginLeft: '-8px' }} />
      </Box>

      {/* ENTRY SENT! */}
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 800,
          fontSize: '34px',
          letterSpacing: 3,
          lineHeight: 1.1,
          textAlign: 'center',
          mb: 1,
        }}
      >
        ENTRY SENT!
      </Typography>

      {/* サブタイトル */}
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.9)',
          fontWeight: 400,
          fontSize: '20px',
          textAlign: 'center',
          mb: 3,
        }}
      >
        店長に通知を送りました！
      </Typography>

      {/* 情報カード（白半透明 opacity 0.1） */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 330,
          bgcolor: 'rgba(255,255,255,0.1)',
          borderRadius: 2.5,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          mb: 4,
        }}
      >
        {/* セクションラベル */}
        <Typography
          sx={{
            color: '#fff',
            fontSize: '20px',
            fontWeight: 700,
            textAlign: 'center',
            mb: 2.5,
            lineHeight: 1.7,
          }}
        >
          この仕事で得られるバリュー
        </Typography>

        {/* ¥アイコン行（予測報酬） */}
        {prediction && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '8px',
                border: '1.5px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <YenIcon width={20} height={20} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', mb: 0.25 }}>
                予測報酬
              </Typography>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '15px' }}>
                {prediction.predicted_reward_min === prediction.predicted_reward_max
                  ? `¥${prediction.predicted_reward_max.toLocaleString()}`
                  : `¥${prediction.predicted_reward_min.toLocaleString()}〜¥${prediction.predicted_reward_max.toLocaleString()}`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* 予測獲得スキル行 */}
        {prediction && prediction.predicted_skills.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '8px',
                border: '1.5px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HandStarIcon width={20} height={20} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', mb: 0.5 }}>
                予測獲得スキル
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {prediction.predicted_skills.map((sk, idx) => (
                  <Chip
                    key={`${sk.name}-${idx}`}
                    label={sk.name}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '10px',
                      fontWeight: 700,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* 白ボタン（SVG: white rect rx=8, dark text） */}
      <Button
        onClick={onClose}
        variant="contained"
        disableElevation
        sx={{
          width: '100%',
          maxWidth: 330,
          borderRadius: 2.5,
          py: 1.25,
          fontWeight: 700,
          fontSize: '14px',
          bgcolor: '#fff',
          color: '#3c3c3c',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        求人一覧に戻る
      </Button>
    </Backdrop>
  );
};

export default EntrySentCelebration;
